import { useEffect, useState } from 'react';
import {
  EventAudience,
  EventAudienceLabel,
  EventEntity,
  EventStatus,
  EventStatusLabel,
  useCreateEventMutation,
  useDeleteEventMutation,
  useFindOneEventQuery,
  useUpdateEventMutation,
} from 'src/store/eventApi';
import { Field } from 'src/ui/Field';
import { Input } from 'src/ui/Input';
import { Textarea } from 'src/ui/Textarea';
import { Select } from 'src/ui/Select';
import { Button } from 'src/ui/Button';
import { Spinner } from 'src/ui/Spinner';
import { useToast } from 'src/ui/Toast';
import { NEW_EVENT_ID } from './index';

interface EditorProps {
  eventId: string | null;
  onCreated: (id: string) => void;
  onDeleted: () => void;
}

interface FormState {
  title: string;
  description: string;
  startAt: string;
  endAt: string;
  location: string;
  capacityMode: CapacityMode;
  capacity: string;
  maleCapacity: string;
  femaleCapacity: string;
  status: EventStatus;
  audience: EventAudience;
  joinCode: string;
}

/**
 * How an event limits sign-ups. Per-side is the default: a matchmaking event
 * filled first-come from a shared pool ends up seventeen men and three women,
 * which fails as an event. A shared total is for anything that isn't paired.
 */
type CapacityMode = 'gender' | 'total';

const blankForm = (): FormState => ({
  title: '',
  description: '',
  startAt: '',
  endAt: '',
  location: '',
  capacityMode: 'gender',
  capacity: '',
  maleCapacity: '',
  femaleCapacity: '',
  status: EventStatus.DRAFT,
  audience: EventAudience.ALL,
  joinCode: '',
});

// Short, easy-to-read-aloud code — no ambiguous chars (0/O, 1/I).
const generateJoinCode = (): string => {
  const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let code = '';
  for (let i = 0; i < 6; i++) {
    code += alphabet[Math.floor(Math.random() * alphabet.length)];
  }
  return code;
};

// Convert ISO timestamp → "YYYY-MM-DDTHH:mm" for <input type=datetime-local>.
const isoToLocalInput = (iso: string | undefined | null): string => {
  if (!iso) return '';
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours()
  )}:${pad(d.getMinutes())}`;
};

const localInputToIso = (local: string): string | undefined => {
  if (!local) return undefined;
  return new Date(local).toISOString();
};

const fromEntity = (e: EventEntity): FormState => ({
  title: e.title ?? '',
  description: e.description ?? '',
  startAt: isoToLocalInput(e.startAt),
  endAt: isoToLocalInput(e.endAt),
  location: e.location ?? '',
  // An event written before the split, or one deliberately capped as a whole,
  // reads back as `total`; anything with a side cap reads back as `gender`.
  capacityMode:
    e.maleCapacity != null || e.femaleCapacity != null ? 'gender' : 'total',
  capacity: e.capacity != null ? String(e.capacity) : '',
  maleCapacity: e.maleCapacity != null ? String(e.maleCapacity) : '',
  femaleCapacity: e.femaleCapacity != null ? String(e.femaleCapacity) : '',
  status: e.status,
  audience: e.audience ?? EventAudience.ALL,
  joinCode: e.joinCode ?? '',
});

const toPayload = (form: FormState): Partial<EventEntity> => ({
  title: form.title,
  description: form.description,
  startAt: localInputToIso(form.startAt),
  endAt: localInputToIso(form.endAt),
  location: form.location,
  // The two modes are exclusive, so the unused fields are cleared rather than
  // left behind — a stale side cap would silently outrank the shared number.
  // In per-side mode `capacity` carries the sum: it is the fallback for a
  // guest neither side cap covers (no gender on their profile, or non-binary),
  // and without it those sign-ups would be unlimited.
  ...(form.capacityMode === 'gender'
    ? {
        maleCapacity: Number(form.maleCapacity),
        femaleCapacity: Number(form.femaleCapacity),
        capacity: Number(form.maleCapacity) + Number(form.femaleCapacity),
      }
    : {
        maleCapacity: null,
        femaleCapacity: null,
        capacity: form.capacity ? Number(form.capacity) : null,
      }),
  status: form.status,
  audience: form.audience,
  joinCode: form.audience === EventAudience.CODE ? form.joinCode || null : null,
});

const Editor = ({ eventId, onCreated, onDeleted }: EditorProps) => {
  const toast = useToast();
  const isNew = eventId === NEW_EVENT_ID;
  const isEmpty = !eventId;

  const { data: existing, isFetching } = useFindOneEventQuery(eventId ?? '', {
    skip: isEmpty || isNew,
  });
  const [createEvent, createStatus] = useCreateEventMutation();
  const [updateEvent, updateStatus] = useUpdateEventMutation();
  const [deleteEvent, deleteStatus] = useDeleteEventMutation();

  const [form, setForm] = useState<FormState>(blankForm);

  useEffect(() => {
    if (isNew) setForm(blankForm());
    else if (existing) setForm(fromEntity(existing));
    else if (isEmpty) setForm(blankForm());
  }, [eventId, existing, isNew, isEmpty]);

  if (isEmpty) {
    return (
      <div
        style={{
          padding: 24,
          height: '100%',
          overflow: 'auto',
          color: 'var(--text-muted)',
          fontSize: 'var(--fs-sm)',
        }}
      >
        Select an event from the list, or click <b>New Event</b> to create one.
      </div>
    );
  }

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  const seatTotal =
    Number(form.maleCapacity || 0) + Number(form.femaleCapacity || 0);

  const onSave = async () => {
    if (!form.title.trim()) {
      toast({ type: 'error', title: 'Title is required' });
      return;
    }
    if (!form.startAt) {
      toast({ type: 'error', title: 'Start time is required' });
      return;
    }
    // Guests like or pass their matches for a week after the end time, so an
    // event without one leaves them no window at all — the server rejects it.
    if (!form.endAt) {
      toast({ type: 'error', title: 'End time is required' });
      return;
    }
    // `datetime-local` values are fixed-width YYYY-MM-DDTHH:mm, so comparing
    // them as strings orders them correctly without parsing.
    if (form.endAt <= form.startAt) {
      toast({ type: 'error', title: 'End time must be after the start time' });
      return;
    }
    if (form.capacityMode === 'gender') {
      const male = Number(form.maleCapacity);
      const female = Number(form.femaleCapacity);
      // Both or neither: one side left blank falls back to the shared number,
      // which counts every seat rather than that side's, and the uncapped side
      // can then run away — the exact thing the split is for.
      if (!(male > 0) || !(female > 0)) {
        toast({
          type: 'error',
          title: 'Set seats for both sides',
          message: 'Or switch to a shared total.',
        });
        return;
      }
    }
    if (form.audience === EventAudience.CODE && !form.joinCode.trim()) {
      toast({
        type: 'error',
        title: 'Join code is required for code-gated events',
      });
      return;
    }
    try {
      const payload = toPayload(form);
      if (isNew) {
        const created = await createEvent(payload).unwrap();
        toast({ type: 'success', title: 'Event created' });
        onCreated(created.id);
      } else if (eventId) {
        await updateEvent({ id: eventId, ...payload }).unwrap();
        toast({ type: 'success', title: 'Event saved' });
      }
    } catch (err: any) {
      toast({
        type: 'error',
        title: 'Save failed',
        message: err?.data?.message || err?.message || String(err),
      });
    }
  };

  const onDelete = async () => {
    if (isNew || !eventId) return;
    if (!window.confirm('Delete this event? This cannot be undone.')) return;
    try {
      await deleteEvent(eventId).unwrap();
      toast({ type: 'success', title: 'Event deleted' });
      onDeleted();
    } catch (err: any) {
      toast({
        type: 'error',
        title: 'Delete failed',
        message: err?.data?.message || err?.message || String(err),
      });
    }
  };

  const saving = createStatus.isLoading || updateStatus.isLoading;

  return (
    <div style={{ height: '100%', overflow: 'auto', padding: 22 }}>
      <div style={{ maxWidth: 680 }}>
        {isFetching && (
          <div style={{ marginBottom: 12 }}>
            <Spinner size="sm" />
          </div>
        )}

        <div className="col gap-4">
          <Field label="Event title" htmlFor="event-title">
            <Input
              id="event-title"
              value={form.title}
              onChange={(e) => set('title', e.target.value)}
              placeholder="Event title"
            />
          </Field>

          <Field label="Description">
            <Textarea
              value={form.description}
              onChange={(e) => set('description', e.target.value)}
              rows={3}
              placeholder="Describe the event…"
            />
          </Field>

          <Field label="Location">
            <Input
              value={form.location}
              onChange={(e) => set('location', e.target.value)}
              placeholder="Venue or address"
            />
          </Field>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 16,
            }}
          >
            <Field label="Start" htmlFor="event-start">
              <Input
                id="event-start"
                type="datetime-local"
                value={form.startAt}
                onChange={(e) => set('startAt', e.target.value)}
              />
            </Field>
            <Field
              label="End"
              htmlFor="event-end"
              hint="Match like/pass opens here, for 7 days"
            >
              <Input
                id="event-end"
                type="datetime-local"
                value={form.endAt}
                onChange={(e) => set('endAt', e.target.value)}
              />
            </Field>
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 16,
            }}
          >
            <Field
              label="Seats"
              hint={
                form.capacityMode === 'gender'
                  ? `Each side has its own seats and its own waitlist.${
                      seatTotal ? ` ${seatTotal} guests in total.` : ''
                    }`
                  : 'One pool, first come first served. Leave blank for no limit.'
              }
            >
              <div className="col gap-2">
                <Select
                  value={form.capacityMode}
                  onChange={(e) =>
                    set('capacityMode', e.target.value as CapacityMode)
                  }
                  style={{ maxWidth: 220 }}
                >
                  <option value="gender">By gender</option>
                  <option value="total">Shared total</option>
                </Select>
                {form.capacityMode === 'gender' ? (
                  <div className="row gap-2">
                    <Input
                      type="number"
                      min={1}
                      value={form.maleCapacity}
                      onChange={(e) => set('maleCapacity', e.target.value)}
                      placeholder="Male"
                      style={{ maxWidth: 106 }}
                    />
                    <Input
                      type="number"
                      min={1}
                      value={form.femaleCapacity}
                      onChange={(e) => set('femaleCapacity', e.target.value)}
                      placeholder="Female"
                      style={{ maxWidth: 106 }}
                    />
                  </div>
                ) : (
                  <Input
                    type="number"
                    min={0}
                    value={form.capacity}
                    onChange={(e) => set('capacity', e.target.value)}
                    style={{ maxWidth: 160 }}
                  />
                )}
              </div>
            </Field>
            <Field label="Status">
              <Select
                value={form.status}
                onChange={(e) =>
                  set('status', Number(e.target.value) as EventStatus)
                }
              >
                {Object.entries(EventStatusLabel).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </Select>
            </Field>
          </div>

          <Field
            label="Who can register"
            hint={
              form.audience === EventAudience.ALL
                ? 'Any user can self-register once published.'
                : form.audience === EventAudience.TARGETED
                ? 'Only users invited from the Attendees tab (via "Invite by filter" or "Add attendees") can register.'
                : 'Users must enter the join code below to register.'
            }
          >
            <Select
              value={form.audience}
              onChange={(e) =>
                set('audience', Number(e.target.value) as EventAudience)
              }
            >
              {Object.entries(EventAudienceLabel).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </Select>
          </Field>

          {form.audience === EventAudience.CODE && (
            <Field label="Join code">
              <div className="row gap-2">
                <Input
                  value={form.joinCode}
                  onChange={(e) =>
                    set('joinCode', e.target.value.toUpperCase())
                  }
                  placeholder="e.g. SALON26"
                  style={{ maxWidth: 200 }}
                />
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => set('joinCode', generateJoinCode())}
                >
                  Generate
                </Button>
              </div>
            </Field>
          )}
        </div>

        <div className="divider" style={{ margin: '20px 0 16px' }} />

        <div className="row" style={{ justifyContent: 'space-between' }}>
          {!isNew ? (
            <Button
              variant="danger"
              leftIcon="trash"
              onClick={onDelete}
              loading={deleteStatus.isLoading}
            >
              Delete event
            </Button>
          ) : (
            <div />
          )}
          <div className="row gap-2">
            <Button
              variant="ghost"
              onClick={() =>
                setForm(existing ? fromEntity(existing) : blankForm())
              }
            >
              Discard
            </Button>
            <Button
              variant="primary"
              leftIcon="save"
              loading={saving}
              onClick={onSave}
            >
              {isNew ? 'Create event' : 'Save event'}
            </Button>
          </div>
        </div>

        {!isNew && existing && (
          <div
            style={{
              marginTop: 16,
              fontSize: 'var(--fs-xs)',
              color: 'var(--text-muted)',
            }}
          >
            <div>Event ID: {existing.id}</div>
            <div>Created: {new Date(existing.createdAt).toLocaleString()}</div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Editor;
